// Outreach (/staff/outreach): what the old growth tabs held that isn't elsewhere, on one dense page with anchors. Campaigns,
// domains, mailboxes, capacity, channels, experiments, cohorts, learning, lanes and templates. Most of it comes from the last
// snapshot, so each part says how old it is. Template review keeps its flag, its hash and its owner-only, 12-hour rule, all
// of them enforced on the server (pr/growth/control.py).
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useLocation } from 'react-router-dom'
import { api } from '../../../lib'
import { Busy, Button, Err, Page, Section, Strip, Table, errorText, useKit, type Column } from '../kit'
import { useOps } from '../../growth/usePulse'
import type { Board, Channel, Cohort, DayRow, Experiment, Growth, LaneRow, Loop, Page as ProspectPage, Recent, Site, Track, Version } from '../../growth/types'
import { DASH, current, n, plural, readAt, referrer, secs, short, sourceText, sum, today, when } from '../../growth/format'
import { Bar, Delivery, Lines, ProspectLink, Sending, SourceFresh, Ticks, repliesRead, sourceOf, useGrowth, useInternal } from './parts'

const ANCHORS: [string, string][] = [['campaigns', 'Campaigns'], ['domains', 'Domains'], ['mailboxes', 'Mailboxes'], ['capacity', 'Capacity']]
const FOLDED = ['#channels', '#experiments', '#cohorts', '#learning', '#lanes']

export function OutreachPage() {
  const [internal, box] = useInternal()
  const q = useGrowth(internal)
  const ops = useOps()
  const templates = !!ops.data?.flags.templates
  const b = q.data?.outreach?.board ?? null
  const { hash } = useLocation()
  useEffect(() => {
    if (!q.data || !hash) return
    const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' }), 40)
    return () => clearTimeout(t)
  }, [q.data, hash, templates])
  return (
    <Page title="Outreach" testid="growth-channels-page" right={box}>
      <nav className="st-strip" aria-label="On this page" data-testid="outreach-anchors">
        {[...ANCHORS, ...(templates ? [['templates', 'Templates'] as [string, string]] : [])].map(([id, label]) => <a key={id} href={`#${id}`}>{label}</a>)}
        <Link to="/staff/funnel" data-testid="outreach-to-funnel">Funnel</Link>
      </nav>
      {q.isLoading ? <Busy what="Reading outreach" /> : q.error ? <Err error={q.error} /> : !q.data?.outreach ? <p className="st-note">No outreach data yet. Signups and card requests still update live</p> : (
        <>
          <Head g={q.data} b={b} />
          <Delivery g={q.data} />
          {b ? <Sending b={b} ready={ops.data?.state.ready_next} id="capacity" /> : null}
          {templates ? <Templates /> : null}
          {b ? <Rest g={q.data} b={b} open={FOLDED.includes(hash)} /> : null}
        </>
      )}
    </Page>
  )
}

/** What a founder doesn't act on every day. Parts with something in them fold up here. Parts with nothing say so in one line. */
function Rest({ g, b, open }: { g: Growth; b: Board; open: boolean }) {
  const experiments = b.experiments.length > 0 || b.cohorts.length > 0
  const learning = b.learning.some((r) => r.have !== null)
  const lanes = b.lanes.read
  const channels = b.deals !== null || !!b.attribution || (g.outreach?.pages.length ?? 0) > 0
  const nothing = [!channels && 'Channels', !experiments && 'Experiments', !learning && 'Learning', !lanes && 'Lanes'].filter((x): x is string => !!x)
  const held = [channels && 'channels', experiments && 'experiments', learning && 'learning', lanes && 'lanes'].filter((x): x is string => !!x)
  return (
    <>
      {held.length ? (
        <details className="st-td-details" data-testid="outreach-more" open={open}>
          <summary>More: {held.join(', ')}</summary>
          <div className="st-stack st-fn-email">
            {channels ? <Channels g={g} b={b} /> : null}
            {experiments ? <Experiments b={b} /> : null}
            {learning ? <Learning rows={b.learning} /> : null}
            {lanes ? <Lanes b={b} /> : null}
          </div>
        </details>
      ) : null}
      {nothing.length ? <p className="st-note" data-testid="outreach-nothing">Nothing read yet: {nothing.join(', ')}</p> : null}
    </>
  )
}

export function emailsLine(b: Board): { title: string; sub: string } {
  const f = b.funnel
  if (!f || f.sent === null) return { title: 'No outreach data yet', sub: 'Signups and card requests still update live' }
  const source = sourceOf(b, 'instantly')
  if (!current(source)) return { title: `Outreach numbers stop at ${short(source?.as_of)}`, sub: `${n(f.sent)} sent by then. Emails sent after that aren't shown` }
  if (f.sent === 0) return { title: 'No email has gone out yet', sub: f.still_in === null ? 'Campaigns not read' : `${plural(f.still_in, 'company', 'companies')} are in campaigns` }
  const sentToday = b.sends.by_day.find((d) => d.date === today())?.sent ?? 0
  const sent = sentToday && sentToday === f.sent ? `${n(f.sent)} sent today` : sentToday ? `${n(f.sent)} sent, ${n(sentToday)} today` : `${n(f.sent)} sent`
  const bounced = f.bounced === null ? 'bounces not read' : f.bounced === 0 ? 'none bounced' : `${n(f.bounced)} bounced`
  const still = f.still_in === null ? '' : `, ${n(f.still_in)} still in campaigns`
  const title = f.replied === null ? 'Replies not read' : f.replied ? plural(f.replied, 'reply', 'replies') : 'No replies yet'
  return { title, sub: `${sent}, ${bounced}${still}` }
}

function Head({ g, b }: { g: Growth; b: Board | null }) {
  const emails = sourceOf(b, 'instantly')
  const line = b ? emailsLine(b) : null
  const strip = b?.groups.filter((x) => (x.sent ?? 0) > 0) ?? []
  const late = b?.sources.filter((s) => !current(s)) ?? []
  return (
    <div className="st-stack" data-testid="growth-answer-emails">
      {line ? <p data-testid={b && !current(emails) ? 'growth-stale' : undefined}><span className="st-strong">{line.title}</span> <span className="st-muted" data-testid="growth-sent">{line.sub}</span></p> : null}
      {current(emails) && strip.length ? (
        <div className="st-fn-strips" data-testid="growth-strip">
          {strip.map((x) => <span key={x.key} style={{ flexGrow: x.sent ?? 1 }}><Ticks total={x.sent ?? 0} on={x.still_in} name={`${x.label}, still in a campaign`} /><span className="st-note">{x.label} {n(x.still_in)} of {n(x.sent)}</span></span>)}
        </div>
      ) : null}
      <SourceFresh s={emails} label="Emails" testid="outreach-read" />
      <details className="st-td-details" data-testid="growth-fresh">
        <summary>Sources{late.length ? `, ${late.length} old` : ''}</summary>
        <div className="st-lines">
          {b?.sources.map((s) => (
            <div key={s.id} className="st-line" data-testid={`growth-source-${s.id}`}>
              <span>{s.label}{s.note ? <span className="st-muted"> {s.note}</span> : null}</span><span className="st-muted">{sourceText(s)}</span>
            </div>
          ))}
          {b?.built_at ? <div className="st-line"><span>Snapshot built</span><span className="st-muted">{readAt(b.built_at)}</span></div> : null}
          {b?.problems?.map((p, i) => <p key={i} className="st-note">{p}</p>)}
        </div>
      </details>
    </div>
  )
}

function dealsLine(b: Board): string {
  const rows = b.channels.filter((c) => c.deals !== null)
  const total = sum(rows.map((c) => c.deals)) ?? 0
  const started = b.sends.first_day ? `Cold email started ${short(b.sends.first_day)}` : 'No cold email has gone out yet'
  if (b.deals === null) return `Attio was not read. ${started}`
  if (total === 0) return `No deals in Attio yet. ${started}`
  const cold = rows.find((c) => c.key === 'cold_email')?.deals ?? 0
  return `${plural(total, 'deal')} in Attio, ${n(cold)} from cold email. ${started}`
}

function status(c: Channel, b: Board): string {
  if (c.key === 'cold_email') return b.funnel?.sent ? `${n(b.funnel.sent)} emailed` : 'Not started'
  if (c.key === 'ploy_visitors') return c.leads === null ? DASH : `${n(c.leads)} leads found, ${n(c.loaded)} loaded`
  if (c.key === 'founder_ads') return c.page_views || c.deals ? 'Has views or deals' : 'Not started'
  return (c.deals ?? 0) > 0 ? 'Has deals' : DASH
}

type Cut = 'pages' | 'sites' | 'days' | 'signups'
const STATUS: Record<string, string> = { draft: 'Started', received: 'Sent', invited: 'Invited', withdrawn: 'Withdrawn' }
const VIA: Record<string, string> = { google: 'Google', microsoft: 'Microsoft', email: 'Email link', dev: 'Dev' }
const signupSource = (r: Recent) => [r.utm_source, r.utm_campaign].filter(Boolean).join(' / ') || (r.utm_start ? `start ${r.utm_start}` : '') || (r.via ? VIA[r.via] ?? r.via : DASH)

function Channels({ g, b }: { g: Growth; b: Board }) {
  const [cut, setCut] = useState<Cut>('pages')
  const max = Math.max(1, ...b.channels.map((c) => c.deals ?? 0))
  const o = g.outreach
  const a = b.attribution
  const parts = a ? [{ key: 'none', label: 'Untagged', v: a.untagged ?? 0 }, { key: 'warm', label: 'Warm', v: a.warm ?? 0 }, { key: 'cold', label: 'Cold email', v: a.cold_email ?? 0 }].filter((p) => p.v > 0) : []
  const total = parts.reduce((t, p) => t + p.v, 0)
  const share = a && total ? Math.round(((a.untagged ?? 0) / total) * 100) : null
  const refs = o?.site.referrers ?? []
  const pageCols: Column<ProspectPage>[] = [
    { key: 'company', head: 'Company', primary: true, cell: (r) => <ProspectLink domain={r.domain}>{r.company ?? r.slug}</ProspectLink> }, { key: 'contact', head: 'Contact', cell: (r) => r.contact ?? DASH },
    { key: 'visits', head: 'Visits', num: true, cell: (r) => n(r.visits) }, { key: 'time', head: 'Time', num: true, cell: (r) => secs(r.time_s) }, { key: 'cta', head: 'CTA clicks', num: true, cell: (r) => n(r.cta_clicks) },
    { key: 'booked', head: 'Booked', cell: (r) => (r.booked ? 'Yes' : '') }, { key: 'seen', head: 'Last seen', cell: (r) => short(r.last_seen) },
  ]
  const siteCols: Column<Site>[] = [
    { key: 'host', head: 'Site', primary: true, cell: (r) => r.host }, { key: 'visitors', head: 'Visitors', num: true, cell: (r) => n(r.visitors) }, { key: 'views', head: 'Page views', num: true, cell: (r) => n(r.pageviews) },
    { key: 'sessions', head: 'Sessions', num: true, cell: (r) => n(r.sessions) }, { key: 'time', head: 'Time', num: true, cell: (r) => secs(r.time_s) },
  ]
  const dayCols: Column<DayRow>[] = [
    { key: 'day', head: 'Day', primary: true, cell: (r) => short(r.day) }, { key: 'v', head: 'Site visitors', num: true, cell: (r) => n(r.site_visitors) }, { key: 'p', head: 'Prospect page views', num: true, cell: (r) => n(r.page_views) },
    { key: 'b', head: 'Calls booked', num: true, cell: (r) => n(r.booked) }, { key: 's', head: 'Signups', num: true, cell: (r) => n(r.signups) }, { key: 'st', head: 'Requests started', num: true, cell: (r) => n(r.started) }, { key: 'f', head: 'Requests sent', num: true, cell: (r) => n(r.finished) },
  ]
  const signupCols: Column<Recent>[] = [
    { key: 'company', head: 'Company', primary: true, cell: (r) => r.company ?? DASH }, { key: 'person', head: 'Person', cell: (r) => <>{r.name ?? r.email}{r.internal ? <span className="st-muted"> internal</span> : null}</> },
    { key: 'at', head: 'Signed up', cell: (r) => when(r.created_at) }, { key: 'source', head: 'Source', cell: signupSource },
    { key: 'card', head: 'Card request', cell: (r) => (r.request_status ? `${STATUS[r.request_status] ?? r.request_status}${r.staff_flag ? `, ${r.staff_flag}` : ''}` : '') },
  ]
  return (
    <Section id="channels" title="Channels" testid="growth-channels">
      <p className="st-note">{dealsLine(b)}</p>
      <Table rows={b.channels} rowKey={(c) => c.key} testid="growth-channel-table" columns={[
        { key: 'label', head: 'Channel', primary: true, cell: (c) => c.label },
        { key: 'deals', head: 'Deals in Attio', cell: (c) => <span className="st-bar-row st-fn-deal"><Bar value={c.deals ?? 0} max={max} title={`${c.label}: ${n(c.deals)} deals`} /><span className="st-num">{n(c.deals)}</span></span> },
        { key: 'views', head: 'Page views', num: true, cell: (c) => n(c.page_views) }, { key: 'status', head: 'Status', num: true, cell: (c) => <span className="st-muted">{status(c, b)}</span> },
      ]} />
      <div className="st-two">
        <div data-testid="growth-sources">
          <h3 className="st-fn-step">{!a ? 'Page view sources not read' : share === null ? 'No page views yet' : `${share}% of page views have no source`}{a?.window ? <span>{a.window.split('..').map((d) => short(d.slice(0, 10))).join(' to ')}</span> : null}</h3>
          {total ? <Lines rows={parts.map((p) => [p.label, n(p.v)])} /> : null}
          {refs.length ? <Lines rows={refs.slice(0, 6).map((r) => [referrer(r.name), n(r.sessions)])} /> : null}
        </div>
        <Visitors days={g.days} />
      </div>
      <Strip value={cut} onChange={setCut} testid="growth-channels-cut" items={[{ key: 'pages', label: 'Prospect pages' }, { key: 'sites', label: 'Sites' }, { key: 'days', label: 'Every day' }, { key: 'signups', label: 'Signups' }]} />
      {cut === 'pages' ? <Table rows={o?.pages ?? []} columns={pageCols} rowKey={(r) => r.slug} testid="growth-pages-table" empty="No prospect pages read" /> : null}
      {cut === 'sites' ? <Table rows={o?.sites ?? []} columns={siteCols} rowKey={(r) => r.host} testid="growth-sites" empty="No sites read" /> : null}
      {cut === 'days' ? <Table rows={[...g.days].reverse().slice(0, 30)} columns={dayCols} rowKey={(r) => r.day} testid="growth-days" /> : null}
      {cut === 'signups' ? <Table rows={g.recent} columns={signupCols} rowKey={(r) => r.email} testid="growth-recent-table" empty="No signups yet" /> : null}
    </Section>
  )
}

function Visitors({ days }: { days: DayRow[] }) {
  const rows = useMemo(() => days.slice(-8, -1).map((d) => ({ day: d.day, v: d.site_visitors })), [days])
  const max = Math.max(1, ...rows.map((r) => r.v ?? 0))
  const W = 420, H = 150, base = H - 20, gap = 16, w = (W - gap * (rows.length - 1)) / Math.max(rows.length, 1)
  const peak = rows.findIndex((r) => r.v === max)
  if (rows.every((r) => r.v === null || r.v === undefined)) {
    return <div data-testid="growth-visitors"><h3 className="st-fn-step">Site visitors a day</h3><p className="st-note" data-testid="growth-visitors-unread">Not read for these days</p></div>
  }
  return (
    <div data-testid="growth-visitors">
      <h3 className="st-fn-step">Site visitors a day</h3>
      <svg className="st-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Site visitors a day, last 7 days">
        <line x1={0} x2={W} y1={base} y2={base} className="st-chart-grid" />
        {rows.map((r, i) => {
          const x = i * (w + gap), h = r.v ? Math.max(2, (r.v / max) * (base - 18)) : 0
          return (
            <g key={r.day}>
              <title>{`${short(r.day)}: ${r.v === null ? 'not read' : n(r.v)}`}</title>
              <rect x={x} y={0} width={w} height={base} fill="transparent" />
              {r.v === null ? <rect x={x} y={base - 2} width={w} height={2} className="st-tick-off" /> : <rect x={x} y={base - h} width={w} height={h} rx={3} className={i === peak ? 'st-mark' : 'st-mark-mid'} />}
              {i === peak && r.v ? <text x={x + w / 2} y={base - h - 5} textAnchor="middle" className="st-chart-text">{n(r.v)}</text> : null}
              {i % 3 === 0 ? <text x={x + w / 2} y={H - 4} textAnchor="middle" className="st-chart-text">{short(r.day)}</text> : null}
            </g>
          )
        })}
      </svg>
    </div>
  )
}

const UNIT: Record<string, string> = { lead: 'emailed', page: 'visitors', account: 'accounts' }

/** "2-3 weeks at about 80-100 new leads..." to "2 to 3 weeks". */
export function shortWeeks(s: string): string {
  const head = s.split(/ at | \(/)[0].replace(/(\d)-(\d)/g, '$1 to $2').replace(/,? after EXP-\d+/i, ', after the one above')
  return head ? head[0].toUpperCase() + head.slice(1) : ''
}

function Experiments({ b }: { b: Board }) {
  const rows = b.experiments
  const running = rows.filter((e) => e.status === 'running').length
  const waiting = rows.filter((e) => e.status === 'proposed').length
  const title = rows.length === 0 ? 'No experiments yet' : running ? `${plural(running, 'experiment')} running${waiting ? `, ${n(waiting)} more waiting on your OK` : ''}` : waiting ? `${plural(waiting, 'experiment')} waiting on your OK` : 'None have started'
  const cols: Column<Experiment>[] = [
    { key: 'title', head: 'Test', primary: true, cell: (r) => r.title },
    { key: 'target', head: 'Needs per side', num: true, cell: (r) => (r.target === null ? DASH : `${n(r.target)} ${UNIT[r.unit] ?? r.unit}`) },
    { key: 'weeks', head: 'Time to a result', num: true, cell: (r) => <span className="st-muted">{shortWeeks(r.weeks)}</span> },
  ]
  return (
    <Section id="experiments" title="Experiments" testid="growth-experiments">
      <p className="st-note">{title}</p>
      {rows.length ? <Table rows={rows} columns={cols} rowKey={(r) => r.id} testid="growth-experiment-table" /> : null}
      {b.cohorts.length ? <Cohorts rows={b.cohorts} /> : null}
    </Section>
  )
}

/** Each group's 95% interval: a long bar means we don't know yet. */
function Cohorts({ rows }: { rows: Cohort[] }) {
  const max = 50
  const pos = (v: number | null) => `${(Math.min(v ?? 0, max) / max) * 100}%`
  return (
    <div id="cohorts" data-testid="growth-cohorts">
      <h3 className="st-fn-step">Too few accounts to compare groups<span>Meetings per 100 accounts. A long bar means we don't know yet</span></h3>
      {rows.map((r) => (
        <div key={r.label} className="st-bar-row st-fn-range">
          <span>{r.label}</span>
          <div className="st-bar-track"><div className="st-bar st-range" style={{ left: pos(r.lo), width: `calc(${pos(r.hi)} - ${pos(r.lo)})` }} title={`${r.label}: ${n(r.per_100)} per 100, 95% interval ${n(r.lo)} to ${n(r.hi)}`} /></div>
          <span className="st-num">{n(r.meetings)} of {n(r.accounts)}</span>
        </div>
      ))}
    </div>
  )
}

/** "41 of 64 researched per source", "12 Connor rulings", "1 proven visit, needs clean visit data". */
function sample(r: Loop): string {
  const counted = `${n(r.have)} ${r.have === 1 ? r.unit.replace(/s$/, '') : r.unit}`
  if (r.need === null) {
    const gate = (r.gate ?? '').toLowerCase()
    return r.have === null ? `Needs ${gate}` : `${counted}, needs ${gate}`
  }
  return r.need === 1 || (r.have !== null && r.have >= r.need) ? counted : `${n(r.have)} of ${n(r.need)} ${r.unit}`
}

const verdict = (r: Loop) => (r.running ? 'Running' : r.enough ? 'Can start' : r.need === null ? 'Waiting' : r.have === null ? 'Not read' : 'Too little data')

function Learning({ rows }: { rows: Loop[] }) {
  const running = rows.filter((r) => r.running).length
  const can = rows.filter((r) => r.enough && !r.running).length
  return (
    <Section id="learning" title={running ? `Learning, ${running} of ${rows.length} loops running` : 'Nothing is learning yet'} testid="growth-learning">
      {can ? <p className="st-note">{plural(can, 'loop')} {can === 1 ? 'has' : 'have'} enough data to start</p> : null}
      {rows.map((r) => (
        <div key={r.key} className="st-fn-join st-fn-loop" data-testid={`growth-loop-${r.key}`}>
          <span>{r.label}</span>
          {r.need !== null && r.have !== null ? <Bar value={r.have} max={r.need} /> : <div className="st-bar-track" role="presentation" />}
          <span className="st-muted">{sample(r)}</span>
          <span className={r.running ? undefined : 'st-muted'}>{verdict(r)}</span>
        </div>
      ))}
    </Section>
  )
}

const laneTotal = (rows: LaneRow[]) => sum(rows.flatMap((r) => Object.values(r.n)))

function Lanes({ b }: { b: Board }) {
  const lanes = b.lanes
  const count = (lane: string) => laneTotal([...lanes.startups.rows, ...lanes.partners.rows].filter((r) => r.lane === lane))
  return (
    <Section id="lanes" title="Lanes" testid="growth-lanes">
      <p className="st-note">{lanes.read ? `${n(count('warm'))} warm, ${n(count('proof'))} proof, ${n(count('plain'))} plain` : 'Lanes not read'}</p>
      <TrackRows name="Startups" id="startups" track={lanes.startups} replies={repliesRead(b)} />
      <TrackRows name="Partners" id="partners" track={lanes.partners} replies={repliesRead(b)} />
    </Section>
  )
}

function TrackRows({ name, id, track, replies }: { name: string; id: string; track: Track; replies: boolean }) {
  return (
    <div data-testid={`growth-track-${id}`}>
      <Table rows={track.rows} rowKey={(r) => r.lane} rowAttrs={(r) => ({ 'data-testid': `growth-lane-${id}-${r.lane}` })} columns={[
        { key: 'lane', head: name, primary: true, cell: (r) => <>{r.label} <span className="st-muted">{r.rule}</span></> },
        ...track.stages.map((s): Column<LaneRow> => ({ key: s.key, head: s.label, num: true, cell: (r) => { const v = s.key === 'replied' && !replies ? null : r.n[s.key]; return <span className={v ? undefined : 'st-muted'}>{n(v)}</span> } })),
      ]} />
    </div>
  )
}

type DiffRow = { kind: 'same' | 'add' | 'drop'; text: string }
const lines = (v: Version) => JSON.stringify(v.doc, null, 2).split('\n')

/** A plain line diff: the longest common run, nothing added to the bundle. */
function diff(from: string[], to: string[]): DiffRow[] {
  const best = Array.from({ length: from.length + 1 }, () => new Array<number>(to.length + 1).fill(0))
  for (let i = from.length - 1; i >= 0; i--) {
    for (let j = to.length - 1; j >= 0; j--) best[i][j] = from[i] === to[j] ? best[i + 1][j + 1] + 1 : Math.max(best[i + 1][j], best[i][j + 1])
  }
  const out: DiffRow[] = []
  let i = 0, j = 0
  while (i < from.length || j < to.length) {
    if (i < from.length && j < to.length && from[i] === to[j]) { out.push({ kind: 'same', text: from[i] }); i++; j++ }
    else if (j < to.length && (i === from.length || best[i][j + 1] >= best[i + 1][j])) out.push({ kind: 'add', text: to[j++] })
    else out.push({ kind: 'drop', text: from[i++] })
  }
  return out
}

/** Connor reads the text, the page sends the hash he saw, and the server recomputes it, so an approval can't reach text nobody read. */
function Templates() {
  const client = useQueryClient()
  const { confirm, toast } = useKit()
  const q = useQuery({ queryKey: ['staff', 'growth', 'templates'], queryFn: () => api<{ rows: Version[]; owner: boolean }>('GET', '/api/staff/growth/templates') })
  const [open, setOpen] = useState<string | null>(null)
  const approve = useMutation({
    mutationFn: (v: Version) => api('POST', `/api/staff/growth/templates/${v.id}/approve`, { hash: v.hash }),
    onSuccess: () => { toast('Approved'); client.invalidateQueries({ queryKey: ['staff', 'growth', 'templates'] }) },
    onError: (e) => toast(errorText(e), { bad: true }),
  })
  const rows = q.data?.rows ?? []
  const waiting = rows.filter((r) => r.status === 'draft' && !r.approver_email)
  const picked = rows.find((r) => r.id === open) ?? waiting[0] ?? rows[0] ?? null
  const live = picked ? rows.find((r) => r.grp === picked.grp && r.status === 'live' && r.id !== picked.id) : undefined
  const go = async (v: Version) => {
    const ok = await confirm({ title: 'Approve this text', confirm: 'Approve', body: <>{v.label ?? v.grp}, hash {v.hash.slice(0, 12)}. Approval does not set it live.</> })
    if (ok.ok) approve.mutate(v)
  }
  return (
    <Section id="templates" title={waiting.length ? `${plural(waiting.length, 'template')} to review` : 'No template to review'} testid="growth-templates">
      {q.isLoading ? <Busy what="Loading templates" /> : q.error ? <Err error={q.error} /> : (
        <>
          <Table rows={rows} rowKey={(r) => r.id} cur={picked?.id} onOpen={(r) => setOpen(r.id)} empty="No versions yet" columns={[
            { key: 'label', head: 'Version', primary: true, cell: (r) => r.label ?? r.grp },
            { key: 'state', head: 'State', cell: (r) => (r.approver_email ? `Approved ${short(r.approved_at)}` : r.status === 'draft' ? 'Draft' : r.status === 'live' ? 'Live, not approved' : 'Retired') },
          ]} />
          {picked ? (
            <div className="st-stack" data-testid="growth-template">
              <pre className="st-fn-diff">{diff(live ? lines(live) : [], lines(picked)).map((d, i) => <span key={i} className={d.kind === 'same' ? undefined : d.kind === 'add' ? 'st-fn-add' : 'st-fn-drop'}>{d.text}{'\n'}</span>)}</pre>
              <p className="st-note st-fn-hash">{picked.hash}</p>
              {q.data?.owner && !picked.approver_email ? <div><Button tone="primary" disabled={approve.isPending} onClick={() => go(picked)} data-testid="growth-approve">Approve this text</Button></div> : null}
            </div>
          ) : null}
        </>
      )}
    </Section>
  )
}
